import { MapUtils } from '../../../utility/map-utils.js';
import { DataModel, DataModelBuilder, isSameData, isSameList, SerializationContext, SerializedData } from '../../dto/dto.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { InvoiceAction, InvoiceActionType } from './invoice-action.js';
import { Invoice } from './invoice.js';
import { VersionRange } from '../../core/version-range.js';

export enum InvoiceScope {
  Order = 'Order',
  Supplement = 'Supplement',
  Product = 'Product',
  ApplicationFee = 'ApplicationFee',
  Donation = 'Donation',
  Generic = 'Generic',
}

export class InvoiceGroup extends DataModel {
  static readonly TYPE_ID: string = '94c4537c3a8e47598532f69cd4ada772';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly uniqueId: string,
    public readonly scope: InvoiceScope,
    private readonly actions: InvoiceAction[],
  ) {
    super();
  }

  get $typeId() {
    return InvoiceGroup.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, InvoiceGroup.SCHEMA_VERSION);
  }

  static scopeToUniqueId(scope: InvoiceScope): string {
    return fromEnumAsString(scope).toLowerCase();
  }

  static create(uniqueId: string, invoice: Invoice, scope: InvoiceScope): InvoiceGroup {
    return new InvoiceGroup(
        uniqueId,
        scope,
        [InvoiceAction.asCreate(invoice)],
    );
  }

  static forLegacyInvoice(invoice: Invoice, scope: InvoiceScope): InvoiceGroup {
    return new InvoiceGroup(
        this.scopeToUniqueId(scope),
        scope,
        [InvoiceAction.asCreate(invoice)],
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): InvoiceGroup {
    return new InvoiceGroup(
        MapUtils.getString(map, 'uniqueId'),
        toEnumFromString(MapUtils.getString(map, 'scope'), InvoiceScope),
        MapUtils.getList<InvoiceAction>(map, 'actions', (value) => {
          return context.fromData<InvoiceAction>(value as SerializedData);
        }),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'scope': fromEnumAsString(this.scope),
      'actions': this.actions.map((e) => context.toData(e)),
    };
  }

  clone(): this {
    return new InvoiceGroup(
        this.uniqueId,
        this.scope,
        this.actions.map((e) => e.clone()),
    ) as this;
  }

  isSame(other: InvoiceGroup): boolean {
    if(this === other) {
      return true;
    }

    return this.uniqueId === other.uniqueId &&
      this.scope === other.scope &&
      isSameList(other.actions, this.actions, isSameData);
  }

  hasAnyInvoice(): boolean {
    return this.actions.length > 0;
  }

  hasActiveInvoice(): boolean {
    if(this.actions.length <= 0) return false;

    const lastAction = this.actions[this.actions.length - 1];
    return lastAction.type === InvoiceActionType.Create;
  }

  tryFindActiveInvoice(): Invoice | null {
    const tryFindActiveAction = (): InvoiceAction | null => {
      if(this.actions.length <= 0) return null;

      const lastAction = this.actions[this.actions.length - 1];
      if(lastAction.type != InvoiceActionType.Create) return null;

      return lastAction;
    };

    const activeAction = tryFindActiveAction();
    if(activeAction == null) {
      return null;
    }
    if(activeAction.numberOfInvoices <= 0 || activeAction.numberOfInvoices > 1) {
      return null;
    }

    return activeAction.getInvoice(0);
  }

  addAction(action: InvoiceAction): void {
    this.actions.push(action);
  }

  getInvoicesForExport(): Invoice[] {
    const invoices: Invoice[] = [];
    for(const action of this.actions) {
      invoices.push(...action.getAllInvoices());
    }

    return invoices;
  }

  getInvoicesForCheckout(): Invoice[] {
    const invoices: Invoice[] = [];
    if(this.actions.length > 0 && this.actions[0].type == InvoiceActionType.Create) {
      invoices.push(...this.actions[0].getAllInvoices());
    }

    return invoices;
  }
}

export class InvoiceGroupBuilder extends DataModelBuilder<InvoiceGroup> {
  build(context: SerializationContext, map: SerializedData): InvoiceGroup {
    return InvoiceGroup.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(InvoiceGroup.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(InvoiceGroup.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      const scope = toEnumFromString(MapUtils.getString(map, 'scope'), InvoiceScope);

      map['uniqueId'] = InvoiceGroup.scopeToUniqueId(scope);
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
